/**
 * Deep links between the fixed story and the free canvas (POS2-886, design §10).
 *
 * Dashboard → Explore hands over a window, a granularity, a split and the active filters;
 * Explore → Transactions hands the window and filters back to the proof layer. Both are
 * plain URLs with every piece of state visible — copy one into a message and it opens the
 * same view for the person who receives it.
 *
 * Two vocabularies meet here and must match exactly, because a misnamed param does not
 * error: it opens the page with silently default state, which looks like it worked.
 *  - Explore's own controls come from `useExploreState`: `gran` / `measure` / `split` / `view`.
 *  - Filters come from `useWasteFilters`, shared with Transactions (POS2-852).
 * Params equal to their default are omitted, mirroring `setDefaulted` — so a link carries
 * only what it actually changes.
 *
 * The window travels as inclusive **civil days** (`YYYY-MM-DD`), not instants. The days are
 * the site's, and a bare date means the same thing to every reader; an instant would encode
 * the sender's zone and re-anchor a day out for anyone else (POS2-1036/1039).
 */
import type { ExploreView, Measure, Split } from "@/hooks/use-explore-state";
import type { Lens } from "@/hooks/use-lens";
import type { Granularity } from "@/lib/explore-window";

/** The dashboard's £/kg lens in Explore's vocabulary. Following it matters: landing on a
 * currency chart after reading a kg one silently changes the question. */
export const MEASURE_FOR_LENS: Record<Lens, Measure> = { cost: "gbp", weight: "kg" };

/** Explore's home. One constant so POS2-1046's un-gate has nothing to chase. */
export const EXPLORE_PATH = "/waste/explore";
export const TRANSACTIONS_PATH = "/waste/transactions";

/** The filters a deep link carries, in the shared `useWasteFilters` URL vocabulary. A null
 * or absent value is simply not written — the target page keeps its own default. */
export type LinkFilters = {
  site_id?: string | null;
  kitchen_id?: string | null;
  device_serial?: string | null;
  waste_type?: string | null;
  service_time?: string | null;
  food?: string | null;
  /** Site-local `HH:MM` time-of-day window (POS2-1483) — without it a click-through from a
   * windowed view lands on full-day figures. */
  time_from?: string | null;
  time_to?: string | null;
};

/** An inclusive civil-day window in the site's zone. */
export type LinkWindow = { from: string; to: string };

export type ExploreLink = {
  gran: Granularity;
  window: LinkWindow;
  split?: Split;
  measure?: Measure;
  view?: ExploreView;
  filters?: LinkFilters;
};

// Mirrors useExploreState's own defaults — a param at its default stays out of the URL.
const DEFAULTS = { gran: "daily", measure: "gbp", split: "none", view: "line" } as const;

function writeFilters(params: URLSearchParams, filters: LinkFilters | undefined): void {
  for (const [key, value] of Object.entries(filters ?? {})) {
    if (value) params.set(key, value);
  }
}

function writeWindow(params: URLSearchParams, window: LinkWindow): void {
  params.set("date_from", window.from);
  params.set("date_to", window.to);
}

/**
 * Dashboard → Explore. The window arrives as the site's civil days (`useWasteFilters`'
 * `dayRange`, or a single day for a heatmap cell), and Explore re-anchors it at site-tz
 * midnight exactly as it does a user-picked range.
 */
export function exploreHref({ gran, window, split, measure, view, filters }: ExploreLink): string {
  const params = new URLSearchParams();
  if (gran !== DEFAULTS.gran) params.set("gran", gran);
  if (split && split !== DEFAULTS.split) params.set("split", split);
  if (measure && measure !== DEFAULTS.measure) params.set("measure", measure);
  if (view && view !== DEFAULTS.view) params.set("view", view);
  writeWindow(params, window);
  writeFilters(params, filters);
  return `${EXPLORE_PATH}?${params.toString()}`;
}

/**
 * Explore → the capture feed. Transactions reads the same filter vocabulary, and a custom
 * range is `preset=custom` plus the two bounds — the shape `computeDayRange` recovers.
 */
export function transactionsHref({
  window,
  filters,
}: {
  window: LinkWindow;
  filters?: LinkFilters;
}): string {
  const params = new URLSearchParams({ preset: "custom" });
  writeWindow(params, window);
  writeFilters(params, filters);
  return `${TRANSACTIONS_PATH}?${params.toString()}`;
}
